import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCatalogBySegment,
  selectSelectedProductsByPassengerIndex,
} from 'selectors/productsCartSelectors';
import { useProductsCartSummary } from 'hooks/store/productsCart/useProductsCartSummary';
import { useHasCatalogProducts } from 'hooks/store/productsCart/useHasCatalogProducts';
import { useTotalQuantitiesByProductCode } from 'hooks/store/productsCart/useTotalQuantitiesByProductCode';
import { addProductToCart, updateProductQuantity } from '@/actions/productsCart';
import SnacksModal from 'ui/molecules/SnacksModal';

/**
 * SnacksModalContainer component.
 * Container that connects SnacksModal with Redux state for a specific passenger.
 * Merges full catalog with selected products and quantities.
 * Manages tab state for round trip to switch between outgoing and incoming segments.
 *
 * @param {object} props - Component props
 * @param {number} props.passengerIndex - Index of the passenger
 * @param {boolean} props.isRoundTrip - Indicates if the trip is round trip
 * @param {boolean} props.isOpen - Whether modal is visible.
 * @param {function} props.onClose - Callback when modal closes.
 * @returns {React.ReactNode} SnacksModal component with data and callbacks.
 */
const SnacksModalContainer = ({ passengerIndex, isRoundTrip = false, isOpen = false, onClose }) => {
  const hasOutgoingCatalog = useHasCatalogProducts('outgoing');
  const hasIncomingCatalog = useHasCatalogProducts('incoming');

  const [activeSegment, setActiveSegment] = React.useState(
    !hasOutgoingCatalog && hasIncomingCatalog ? 'incoming' : 'outgoing',
  );

  // Determine if we should show tabs: if isRoundTrip AND at least one segment has products
  const shouldShowTabs = isRoundTrip && (hasOutgoingCatalog || hasIncomingCatalog);

  const catalog = useSelector(selectCatalogBySegment(activeSegment));
  const selectedProducts = useSelector(
    selectSelectedProductsByPassengerIndex(passengerIndex, activeSegment),
  );
  // Get combined totals for round trip or current segment
  const { totalQuantity, totalPrice } = useProductsCartSummary(
    passengerIndex,
    isRoundTrip ? 'both' : activeSegment,
  );
  // Get total quantities selected by all passengers for the active segment
  const totalQuantitiesByCode = useTotalQuantitiesByProductCode(activeSegment);
  const dispatch = useDispatch();

  // Merge catalog with selected quantities
  const catalogWithQuantities = useMemo(() => {
    const selectedMap = selectedProducts.reduce((acc, product) => {
      acc[product.code] = product;
      return acc;
    }, {});

    return catalog.map((catalogProduct) => {
      const selected = selectedMap[catalogProduct.code];
      const currentPassengerQuantity = selected?.quantity || 0;
      const totalQuantitySelected = totalQuantitiesByCode[catalogProduct.code] || 0;

      // Calculate available stock: stock - (totalSelected - currentPassengerQuantity)
      // This gives us the stock available considering other passengers' selections
      const availableStock =
        catalogProduct.stock - (totalQuantitySelected - currentPassengerQuantity);

      return {
        ...catalogProduct,
        quantity: currentPassengerQuantity,
        stock: catalogProduct.stock, // Ensure stock is passed through
        // Disable increase if current passenger's quantity >= available stock
        // or if product is out of stock
        isIncreaseDisabled:
          currentPassengerQuantity >= availableStock || catalogProduct.stock === 0,
        isDecreaseDisabled: !selected,
      };
    });
  }, [catalog, selectedProducts, totalQuantitiesByCode]);

  /**
   * Handle update quantity of a product in the cart
   * @param {string} productCode - Code of the product
   * @param {number} newQuantity - New quantity of the product
   */
  const handleUpdateQuantity = (productCode, newQuantity) => {
    if (newQuantity === 0) {
      // If quantity is 0, remove from cart
      dispatch(
        updateProductQuantity({
          productCode,
          quantity: 0,
          passengerIndex,
          segment: activeSegment,
        }),
      );
    } else if (newQuantity > 0) {
      const product = catalogWithQuantities.find((p) => p.code === productCode);
      const currentQuantity = product?.quantity || 0;

      if (currentQuantity === 0) {
        // Product not in cart, add it
        dispatch(
          addProductToCart({
            product,
            quantity: newQuantity,
            passengerIndex,
            segment: activeSegment,
          }),
        );
      } else {
        // Product already in cart, update it
        dispatch(
          updateProductQuantity({
            productCode,
            quantity: newQuantity,
            passengerIndex,
            segment: activeSegment,
          }),
        );
      }
    }
  };

  /**
   * Handle action button click.
   * Closes the modal.
   */
  const handleActionButtonClick = () => {
    // Close modal when action button is clicked
    onClose();
  };

  /**
   * Handle segment/tab change for round trip
   * @param {string} newSegment - The new segment ('outgoing' or 'incoming')
   */
  const handleSegmentChange = (newSegment) => {
    setActiveSegment(newSegment);
  };

  return (
    <SnacksModal
      isOpen={isOpen}
      onCloseModal={onClose}
      products={catalogWithQuantities}
      onUpdateQuantity={handleUpdateQuantity}
      totalPrice={totalPrice}
      totalQuantity={totalQuantity}
      onActionButtonClick={handleActionButtonClick}
      shouldShowBothTabs={shouldShowTabs}
      hasOutgoingCatalog={hasOutgoingCatalog}
      hasIncomingCatalog={hasIncomingCatalog}
      activeSegment={activeSegment}
      onSegmentChange={handleSegmentChange}
    />
  );
};

SnacksModalContainer.propTypes = {
  passengerIndex: PropTypes.number.isRequired,
  isRoundTrip: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SnacksModalContainer;
